/**
 * <RecordingStudioCard /> — one-click seed of the 45-sec demo state.
 *
 * Backend contract:
 *   GET  /api/admin/recording-demo/status  →  {seeded, seeded_at?, lead_id?, buyer_id?, urls{}}
 *   POST /api/admin/recording-demo/seed    →  seeds + returns urls + ids
 *   POST /api/admin/recording-demo/reset   →  wipes; returns {wiped: {...}}
 *
 * Design goal: Tracy (non-technical) opens the admin dashboard, clicks
 * ONE button, and gets an ordered list of URLs to record against. No
 * terminal, no Python, no SSH.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Clapperboard, Play, RotateCcw, ExternalLink, CheckCircle2,
  AlertTriangle, Loader2, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Ordered shot list — mirrors RECORDING_GUIDE.md
const SHOT_LIST = [
  { key: "landing",       t: "0:00-0:03", label: "Cold open · Landing hero",
    href: "/", note: "Static — just the hero title" },
  { key: "apex_settings", t: "0:03-0:07", label: "Apex is armed",
    hrefKey: "apex_settings", note: "Show the big green toggle + phone binding" },
  { key: "lead_report_1", t: "0:07-0:20", label: "⭐ Live qualifying transcript",
    hrefKey: "lead_report", note: "Slow-scroll: transcript → 4 slot pills → auto-stage card" },
  { key: "lead_report_2", t: "0:20-0:28", label: "Appointment booked",
    hrefKey: "lead_report", note: "Same page — hover Upcoming Appointments card" },
  { key: "pipeline",      t: "0:28-0:35", label: "Pipeline auto-advance",
    hrefKey: "pipeline", note: "Sarah's card sits in 'Hot — Buyer Interested'" },
  { key: "lead_report_3", t: "0:35-0:40", label: "🔔 Match notification",
    hrefKey: "lead_report", note: "Show '1 match · 🔔 1 notified' badges" },
  { key: "dashboard",     t: "0:40-0:44", label: "Payoff metric",
    hrefKey: "dashboard", note: "Morning Brief card + counters" },
  { key: "close",         t: "0:44-0:45", label: "Lockup close",
    href: "/", note: "On-camera you or logo lockup — 'Try it free 14 days'" },
];

function fmtAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const m = Math.floor((Date.now() - then) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function RecordingStudioCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null); // "seed" | "reset" | null

  const load = async () => {
    try {
      const { data } = await api.get("/admin/recording-demo/status");
      setStatus(data);
    } catch { setStatus({ seeded: false, error: true }); }
  };
  useEffect(() => { load(); }, []);

  const seed = async () => {
    setBusy("seed");
    try {
      const { data } = await api.post("/admin/recording-demo/seed");
      setStatus({
        seeded: true, seeded_at: new Date().toISOString(),
        lead_id: data.lead_id, buyer_id: data.buyer_id, urls: data.urls,
      });
      toast.success("Recording state seeded — you're ready to record 🎬");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    }
    setBusy(null);
  };
  const reset = async () => {
    setBusy("reset");
    try {
      await api.post("/admin/recording-demo/reset");
      setStatus({ seeded: false });
      toast.success("Recording state wiped. Ready for a fresh seed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
    setBusy(null);
  };

  const seeded = status?.seeded === true;
  const urls = status?.urls || {};

  return (
    <div
      className="card p-5 border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/40 to-white"
      data-testid="recording-studio-card"
    >
      <header className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-emerald-700 flex items-center gap-1.5">
            <Clapperboard size={11} /> Recording studio
          </div>
          <div className="font-display font-extrabold text-xl mt-0.5">
            One-click 45-sec demo seed
          </div>
          <div className="text-xs text-[var(--ink-2)] mt-1">
            Populates a picture-perfect Apex 2.0 showcase state — lead,
            transcript, appointment, matched buyer, "notified" badge.
            {seeded && status.seeded_at && (
              <> <span className="font-bold text-emerald-700">Seeded {fmtAgo(status.seeded_at)}.</span></>
            )}
          </div>
        </div>
        {seeded ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-extrabold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300">
            <CheckCircle2 size={10} /> Ready
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-extrabold px-2 py-1 rounded-full bg-stone-100 text-stone-600 border border-stone-300">
            Not seeded
          </span>
        )}
      </header>

      {/* Primary CTA row */}
      <div className="flex gap-2 mb-4">
        <button
          type="button"
          onClick={seed}
          disabled={busy != null}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-bold text-sm transition"
          data-testid="recording-studio-seed-btn"
        >
          {busy === "seed" ? (
            <><Loader2 size={13} className="animate-spin" /> Seeding…</>
          ) : (
            <><Play size={13} fill="currentColor" /> {seeded ? "Re-seed (fresh)" : "Seed recording state"}</>
          )}
        </button>
        {seeded && (
          <button
            type="button"
            onClick={reset}
            disabled={busy != null}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-white hover:bg-stone-50 border-2 border-stone-200 text-stone-700 font-bold text-sm transition"
            data-testid="recording-studio-reset-btn"
            title="Remove the seeded demo state"
          >
            {busy === "reset" ? (
              <><Loader2 size={13} className="animate-spin" /> Resetting…</>
            ) : (
              <><RotateCcw size={13} /> Reset</>
            )}
          </button>
        )}
      </div>

      {/* Shot list — only when seeded */}
      {seeded && (
        <>
          <div className="mb-3">
            <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-[var(--ink-2)] mb-2">
              Shot list · record top → bottom
            </div>
            <ol className="space-y-2" data-testid="recording-studio-shot-list">
              {SHOT_LIST.map((s, idx) => {
                const href = s.href || urls[s.hrefKey] || "/";
                return (
                  <li
                    key={s.key + idx}
                    className="flex items-center gap-3 p-2.5 rounded-lg bg-white border border-stone-200 hover:border-emerald-300 transition"
                    data-testid={`recording-studio-shot-${s.key}`}
                  >
                    <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 font-extrabold text-xs flex items-center justify-center flex-shrink-0">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-emerald-700 font-bold">{s.t}</span>
                        <span className="text-sm font-bold truncate">{s.label}</span>
                      </div>
                      <div className="text-[11px] text-[var(--ink-3)] truncate">{s.note}</div>
                    </div>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-700 hover:text-emerald-900 flex-shrink-0"
                      title={href}
                    >
                      <ExternalLink size={14} />
                    </a>
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="text-[11px] text-[var(--ink-2)] p-3 rounded-lg bg-stone-50 border border-stone-200 flex items-start gap-2">
            <span className="text-lg leading-none">💡</span>
            <div>
              <b>Prep:</b> close all other tabs, mute Slack/Email, zoom
              browser to 110%, set viewport 1920×1080. After you're
              done recording, click <b>Reset</b> to clean up.
              Full storyboard + editing guide in{" "}
              <code className="bg-white px-1.5 py-0.5 rounded border border-stone-200">/app/RECORDING_GUIDE.md</code>.
            </div>
          </div>
        </>
      )}

      {!seeded && !status?.error && (
        <div className="text-[11px] text-[var(--ink-2)] p-3 rounded-lg bg-stone-50 border border-stone-200 flex items-start gap-2">
          <span className="text-lg leading-none">🎬</span>
          <div>
            Click <b>"Seed recording state"</b> above — takes 1 second.
            You'll get a numbered shot list of 8 URLs to record against.
          </div>
        </div>
      )}

      {status?.error && (
        <div className="text-[11px] text-red-700 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2">
          <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
          <span>Recording studio unavailable. Try refreshing the page.</span>
        </div>
      )}
    </div>
  );
}

// Utility kept even though the shot list uses direct <a> links — makes
// it easy to add a "copy URL" button later without another import.
export function _copy(text) {
  try {
    navigator.clipboard.writeText(text);
    toast.success("Link copied");
  } catch { /* noop */ }
}
_copy.Icon = Copy;
_copy.Link = Link;
